import { useMemo } from 'react';
import {
  ScatterChart as RechartsScatter,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { ColumnDef, ScatterMapping, VisualizationConfig } from '@deckspace/types';
import { getColorsForIntent, getThemeColors } from '../../theme/index.js';
import { createChartFormatter } from '../../utils/chart-formatter.js';
import { ChartTooltip } from '../../components/ChartTooltip.js';
import { prepScatterSeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: ScatterMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

export function ScatterPlot({ rows, columnDefs, mapping, config, width, height }: Props) {
  const series = useMemo(
    () =>
      rows.length === 0
        ? { labels: [], datasets: [] }
        : prepScatterSeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const datasets = series.datasets ?? [];
  const xDataset = datasets[0];
  const yDataset = datasets[1];

  const chartData = useMemo(
    () =>
      xDataset && yDataset
        ? xDataset.values.map((x, index) => ({
            x,
            y: yDataset.values[index] ?? 0,
            label: series.labels[index] ?? `P${index + 1}`,
          }))
        : [],
    [xDataset, yDataset, series.labels]
  );

  if (!series.labels.length || !xDataset || !yDataset) {
    return <div className="text-muted-foreground">No data available</div>;
  }

  const colors = getColorsForIntent(config.intent, 1);
  const themeColors = getThemeColors();

  const formatter = createChartFormatter(datasets);
  const formatXValue = (value: number) => formatter(value, 0);
  const formatYValue = (value: number) => formatter(value, 1);

  return (
    <RechartsScatter
      width={width ?? 400}
      height={height ?? 300}
      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border} />
      <XAxis
        type="number"
        dataKey="x"
        name={xDataset.label}
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
        tickFormatter={formatXValue}
      />
      <YAxis
        width="auto"
        type="number"
        dataKey="y"
        name={yDataset.label}
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
        tickFormatter={formatYValue}
      />
      <Tooltip
        content={<ChartTooltip formatter={formatYValue} />}
        cursor={{ strokeDasharray: '3 3' }}
      />
      <Legend wrapperStyle={{ color: themeColors.textPrimary }} />
      <Scatter name={`${xDataset.label} vs ${yDataset.label}`} data={chartData} fill={colors[0]} />
    </RechartsScatter>
  );
}
